// TODO: THIS IS BETTER IMPLEMENTED AS JUST AN EXPOSED UTIL FUNCTION 
// THAT CAN BE CONSUMED BY USERS IN A FUNCTION NODE



/**
 * ArrayCycleNode - Cycles a pattern to fill a given length
 *
 * Outputs pattern[i % pattern.length] for i in 0..length-1.
 * Useful for assigning repeating colors, materials, etc.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { params, internals } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const arrayCycleEndpoints = {
  ...params({
    length:  { value: 9, min: 0, max: 256, step: 1, isInput: true },
    pattern: { dataType: 'array', value: ['red', 'blue', 'yellow'], label: 'Pattern' },
  }),
  ...internals({
    output: { dataType: 'array', isOutput: true, label: 'Output' },
  }),
};

// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

class ArrayCycleNode extends BaseNode<typeof arrayCycleEndpoints> {

  initialize(): void {
    this.compute();
  }

  onParamChanged(_payload: Payload, _endpointId: string): void {
    this.compute();
  }

  private compute(): void {
    const length = this.params.length.value;
    const pattern = this.params.pattern.value as unknown[];

    if (pattern.length === 0 || length === 0) {
      this.internals.output.set([]);
      return;
    }

    const result: unknown[] = [];
    for (let i = 0; i < length; i++) {
      result.push(pattern[i % pattern.length]);
    }

    this.internals.output.set(result);
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const arrayCycleNodeDef: NodeDef<ArrayCycleNode> = {
  type: 'ArrayCycle',
  shortName: 'cycle',
  info: 'Repeats a pattern array to fill a given length. Output[i] = pattern[i % pattern.length].',
  domain: Domain.Data,
  category: 'Array',
  endpoints: arrayCycleEndpoints,
  ctor: ArrayCycleNode,
};
